import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import clsx from 'clsx';
export function AmendmentBadge({ amendmentNumber, amendmentSequence, isCurrent = false, sourceFilename, size = 'xs', className }) {
    // If we have neither an amendment number nor a sequence, there's nothing
    // to render. Caller should null-check before mounting; we defensively
    // return null too.
    const hasSignal = amendmentNumber && amendmentNumber.length > 0 || amendmentSequence !== undefined && amendmentSequence > 0;
    if (!hasSignal && amendmentSequence !== 0) {
        return null;
    }
    const label = formatAmendmentLabel(amendmentNumber, amendmentSequence);
    const tooltip = buildTooltip(label, isCurrent, sourceFilename);
    // Size table — kept tight so the badge never crowds the row metadata.
    const sizing = size === 'sm' ? 'px-1.5 py-0.5 text-[10px]' : 'px-1 py-px text-[9px]';
    // Color treatment driven by isCurrent. Hairline borders only — no fills,
    // no shadows. Emerald is reserved for current; everything else is slate.
    const palette = isCurrent ? 'border-emerald-300 text-emerald-800 bg-emerald-50/40' : 'border-slate-200 text-slate-500 bg-white';
    return /*#__PURE__*/ _jsxs("span", {
        title: tooltip,
        className: clsx('inline-flex items-center gap-0.5 rounded border font-mono uppercase tracking-tight', sizing, palette, className),
        children: [
            label,
            isCurrent && /*#__PURE__*/ _jsx("span", {
                "aria-hidden": "true",
                className: clsx('ml-0.5 text-emerald-600', size === 'sm' ? 'text-[10px]' : 'text-[9px]'),
                children: "★"
            })
        ]
    });
}
/**
 * formatAmendmentLabel produces the short visual label shown on the badge.
 * Empty / missing amendment number with sequence=0 → "Base". Numbered
 * amendments → "AM001" / "AM002" / etc. Anything else (sequence>0 with no
 * number) → "AM" + sequence as a fallback so the badge never reads as
 * empty when there IS provenance to show.
 */ export function formatAmendmentLabel(amendmentNumber, amendmentSequence) {
    const trimmed = (amendmentNumber || '').trim();
    if (trimmed) {
        return `AM${trimmed.padStart(3, '0').replace(/^0+(\d{3})/, '$1')}`;
    }
    if (amendmentSequence !== undefined && amendmentSequence > 0) {
        return `AM${String(amendmentSequence).padStart(3, '0')}`;
    }
    return 'Base';
}
function buildTooltip(label, isCurrent, sourceFilename) {
    const parts = [];
    parts.push(label === 'Base' ? 'Base solicitation' : `Amendment ${label.replace(/^AM0*/, '')}`);
    if (isCurrent) parts.push('current version');
    if (sourceFilename) parts.push(sourceFilename);
    return parts.join(' · ');
}
